// repo/primary-care-connect/frontend/src/pages/Portal/FamilyDoctor/FamilyDoctorLayout.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import "./FamilyDoctorLayout.css";

import { ApiError, listFamilyDoctorRequests } from "../../../api/requests";
import { getFamilyDoctorProfile } from "../../../api/portalFamilyDoctor";

function cls(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

function normalizeStatus(status: string): string {
  const s = String(status || "").trim();
  if (!s) return "";
  const k = s.toLowerCase();

  if (k === "closedacceptedelsewhere") return "ClosedAcceptedElsewhere";
  if (k === "in_review") return "InReview";
  if (k === "notaccepted") return "Declined";
  return s;
}

function countSubmittedRequests(items: unknown[]): number {
  return (items || []).filter((x) => {
    const s = normalizeStatus((x as any)?.status || "").toLowerCase();
    return s === "submitted";
  }).length;
}

function formatDoctorDisplayName(val: unknown) {
  const raw = String(val ?? "").trim();
  if (!raw) return "Dr —";
  const hasPrefix = /^dr\.?\s/i.test(raw);
  return hasPrefix ? raw : `Dr ${raw}`;
}

function pickDoctorNameFromProfile(res: any) {
  // Prefer doctor name fields ONLY (not the signed-in user's displayName/email)
  // because the portal header must show the Family Doctor's name.
  const doctor = res?.doctor || res?.familyDoctor || res?.profile || null;

  const candidates = [
    doctor?.DisplayName,
    doctor?.displayName,
    doctor?.FullName,
    doctor?.fullName,
    doctor?.Name,
    doctor?.name,
    doctor?.FirstName && doctor?.LastName
      ? `${doctor.FirstName} ${doctor.LastName}`
      : null,
    doctor?.firstName && doctor?.lastName
      ? `${doctor.firstName} ${doctor.lastName}`
      : null,
  ];

  for (const c of candidates) {
    const s = String(c ?? "").trim();
    if (s) return s;
  }

  return null;
}

const FamilyDoctorLayout: React.FC = () => {
  const signOutHref = useMemo(() => {
    // Plan: sign out and redirect to homepage
    const postLogout = encodeURIComponent(`${window.location.origin}/`);
    return `/.auth/logout?post_logout_redirect_uri=${postLogout}`;
  }, []);

  // ✅ Wire real doctor's name from profile endpoint
  const [doctorName, setDoctorName] = useState<string>("Dr —");

  // ✅ Sidebar badge count for unified Patient Requests.
  // Public/direct + navigator-forwarded requests are now shown in one inbox.
  // (Verified-only requests are already enforced by the API.)
  const [requestBadgeCount, setRequestBadgeCount] = useState<number | null>(
    null
  );

  // Show "Switch Role" only when user has multiple active roles
  const [hasMultipleRoles, setHasMultipleRoles] = useState(false);

  // Desktop sidebar collapse state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Mobile accordion / drawer state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Prevent state updates after unmount
  const isMountedRef = useRef(true);

  async function refreshBadgeCount() {
    try {
      const allItems = await listFamilyDoctorRequests({ sourceScope: "all" });
      const submittedOnly = countSubmittedRequests(allItems || []);

      if (isMountedRef.current) {
        setRequestBadgeCount(submittedOnly);
      }
    } catch (e) {
      // Fail-quietly: do not break layout rendering if API fails.
      // (User can still access the requests page.)
      if (e instanceof ApiError) {
        // optional: context-specific logging could be added later
      }
      if (isMountedRef.current) {
        setRequestBadgeCount(null);
      }
    }
  }

  async function loadDoctorName() {
    try {
      const res = await getFamilyDoctorProfile();

      const name = pickDoctorNameFromProfile(res);
      if (isMountedRef.current) {
        setDoctorName(formatDoctorDisplayName(name));
      }
    } catch {
      // keep placeholder
      if (isMountedRef.current) setDoctorName("Dr —");
    }
  }

  function closeMobileMenu() {
    setMobileMenuOpen(false);
  }

  function toggleMobileMenu() {
    setMobileMenuOpen((prev) => !prev);
  }

  function toggleSidebarCollapsed() {
    setSidebarCollapsed((prev) => !prev);
  }

  useEffect(() => {
    isMountedRef.current = true;

    // Initial loads
    refreshBadgeCount();
    loadDoctorName();

    // Check if user has multiple roles
    (async () => {
      try {
        const res = await fetch("/api/portal-auth-bootstrap", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (!res.ok || !isMountedRef.current) return;
        const data = await res.json();
        const roles = Array.isArray(data?.roles) ? data.roles : [];
        const activeCount = roles.filter((r: any) => {
          const v = r?.isActive;
          if (v === undefined || v === null) return true;
          if (typeof v === "boolean") return v;
          if (typeof v === "number") return v === 1;
          return String(v).toLowerCase() === "true";
        }).length;
        if (isMountedRef.current) setHasMultipleRoles(activeCount > 1);
      } catch {
        // silent
      }
    })();

    // Auto refresh badges every 60 seconds
    const intervalId = window.setInterval(() => {
      refreshBadgeCount();
    }, 60_000);

    // Refresh when the user returns to the tab
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshBadgeCount();
    };

    // Close mobile menu on desktop resize
    const onResize = () => {
      if (window.innerWidth > 900) {
        setMobileMenuOpen(false);
      }
    };

    // Close mobile menu on Escape
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("resize", onResize);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      isMountedRef.current = false;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={cls(
        "fd-shell",
        sidebarCollapsed && "fd-shell--sidebar-collapsed"
      )}
    >
      {/* Sidebar */}
      <aside
        className={cls(
          "fd-sidebar",
          sidebarCollapsed && "fd-sidebar--collapsed",
          mobileMenuOpen && "fd-sidebar--mobile-open"
        )}
        aria-label="Family Doctor portal navigation"
      >
        <div className="fd-sidebar-header">
          <div className="fd-sidebar-collapse-card">
            <button
              type="button"
              className="fd-sidebar-collapse-button"
              aria-label={
                sidebarCollapsed
                  ? "Expand Family Doctor sidebar"
                  : "Collapse Family Doctor sidebar"
              }
              aria-expanded={!sidebarCollapsed}
              onClick={toggleSidebarCollapsed}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <span aria-hidden="true">{sidebarCollapsed ? "›" : "‹"}</span>
            </button>
          </div>

          <div className="fd-sidebar-top">
            <div className="fd-sidebar-mobile-bar">
              <div className="fd-sidebar-identity-card">
                <div className="fd-sidebar-identity">
                  <div className="fd-doctor-name">{doctorName}</div>
                  <div className="fd-portal-title">Family Doctor Portal</div>
                </div>
              </div>

              <button
                type="button"
                className="fd-mobile-menu-button"
                aria-expanded={mobileMenuOpen}
                aria-controls="fd-portal-nav"
                aria-label={
                  mobileMenuOpen
                    ? "Collapse navigation menu"
                    : "Expand navigation menu"
                }
                onClick={toggleMobileMenu}
              >
                <span className="fd-mobile-menu-button-text">Menu</span>
                <span
                  className={cls(
                    "fd-mobile-menu-button-chevron",
                    mobileMenuOpen && "fd-mobile-menu-button-chevron--open"
                  )}
                  aria-hidden="true"
                >
                  ▾
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          id="fd-portal-nav"
          className={cls(
            "fd-sidebar-accordion",
            mobileMenuOpen && "fd-sidebar-accordion--open"
          )}
        >
          <nav className="fd-nav" aria-label="Family Doctor workspace">
            <div className="fd-nav-section">
              <div className="fd-nav-section-label">Workspace</div>

              <NavLink
                to="/portal/family-doctor"
                end
                className={({ isActive }) =>
                  cls("fd-nav-link", isActive && "active")
                }
                onClick={closeMobileMenu}
                title="Dashboard"
              >
                <span className="fd-nav-link-label">Dashboard</span>
              </NavLink>

              <NavLink
                to="/portal/family-doctor/requests"
                className={({ isActive }) =>
                  cls("fd-nav-link", isActive && "active")
                }
                onClick={closeMobileMenu}
                title="Patient Requests"
              >
                <span className="fd-nav-link-label">Patient Requests</span>

                {requestBadgeCount !== null && requestBadgeCount > 0 && (
                  <span
                    className="fd-nav-badge"
                    aria-label={`${requestBadgeCount} new patient requests`}
                    title={`${requestBadgeCount} new patient requests`}
                  >
                    {requestBadgeCount}
                  </span>
                )}
              </NavLink>

              <a
                href="https://referral.acceptingmd.ca"
                className="fd-nav-link"
                onClick={closeMobileMenu}
                title="Referral Navigator"
              >
                <span className="fd-nav-link-label">Referral Navigator</span>
              </a>
            </div>
          </nav>

          <div className="fd-sidebar-bottom" aria-label="Family Doctor account">
            <div className="fd-sidebar-bottom-card fd-sidebar-bottom-card--account">
              <div className="fd-nav-section-label">Account</div>

              <NavLink
                to="/portal/family-doctor/profile"
                className={({ isActive }) =>
                  cls("fd-nav-link", "fd-nav-link--account", isActive && "active")
                }
                onClick={closeMobileMenu}
                title="Profile"
              >
                <span className="fd-nav-link-label">Profile</span>
              </NavLink>

              {/* Settings link intentionally hidden in v1 — the page has no
                  actionable controls yet. Route stays active for deep links.
                  Re-expose this link once a real preference lands. */}
            </div>

            <div
              className="fd-sidebar-bottom-card fd-sidebar-bottom-card--actions"
              aria-label="Portal actions"
            >
              <div className="fd-sidebar-actions-title">Portal actions</div>

              <div className="fd-sidebar-actions-grid">
                <NavLink
                  to="/public"
                  className="fd-side-link"
                  onClick={closeMobileMenu}
                  title="Public Site"
                >
                  <span className="fd-side-link-label">Public Site</span>
                </NavLink>

                {/* ✅ Switch role (Phase-4 multi-role UX) — only if user has 2+ roles */}
                {hasMultipleRoles && (
                  <NavLink
                    to="/portal/select-role"
                    className="fd-side-link"
                    onClick={closeMobileMenu}
                    title="Switch Role"
                  >
                    <span className="fd-side-link-label">Switch Role</span>
                  </NavLink>
                )}

                {/* ✅ Real SWA logout (clears auth cookie) */}
                <a
                  href={signOutHref}
                  className={cls("fd-side-link", "fd-side-link--primary")}
                  onClick={closeMobileMenu}
                  title="Sign Out"
                >
                  <span className="fd-side-link-label">Sign Out</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content: NO top bar so embedded pages start at the top */}
      <div
        className={cls(
          "fd-main",
          sidebarCollapsed && "fd-main--sidebar-collapsed"
        )}
      >
        <main className="fd-content" aria-label="Portal content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default FamilyDoctorLayout;